"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";

type GameEditorLeaveGuardContextValue = {
    shouldConfirmLeave: boolean;
    setShouldConfirmLeave: (value: boolean) => void;
    isEditorActive: boolean;
    setEditorActive: (value: boolean) => void;
};

const GameEditorLeaveGuardContext = createContext<GameEditorLeaveGuardContextValue | null>(null);

export function GameEditorLeaveGuardProvider({ children }: { children: ReactNode }) {
    const [shouldConfirmLeave, setShouldConfirmLeaveState] = useState(false);
    const [isEditorActive, setEditorActiveState] = useState(false);

    const setShouldConfirmLeave = useCallback((value: boolean) => {
        setShouldConfirmLeaveState(value);
    }, []);

    const setEditorActive = useCallback((value: boolean) => {
        setEditorActiveState(value);
    }, []);

    const value = useMemo(
        () => ({
            shouldConfirmLeave,
            setShouldConfirmLeave,
            isEditorActive,
            setEditorActive,
        }),
        [shouldConfirmLeave, setShouldConfirmLeave, isEditorActive, setEditorActive]
    );

    return (
        <GameEditorLeaveGuardContext.Provider value={value}>
            {children}
        </GameEditorLeaveGuardContext.Provider>
    );
}

export function useGameEditorLeaveGuard() {
    return useContext(GameEditorLeaveGuardContext);
}

export function useReportGameEditorLeaveGuard(shouldConfirmLeave: boolean) {
    const context = useGameEditorLeaveGuard();

    useEffect(() => {
        if (!context) {
            return;
        }

        context.setShouldConfirmLeave(shouldConfirmLeave);

        return () => {
            context.setShouldConfirmLeave(false);
        };
    }, [context, shouldConfirmLeave]);
}
